import { dispatch, getState } from '../../store';
import startDrag from '../../dragState/actionCreators/startDrag';
import stopDrag from '../../dragState/actionCreators/stopDrag';
import { isDragging } from '../../dragState/selectors';
import * as pointerPosStore from './pointerPosStore';
import HitAreaManager from './HitAreaManager';

class Draggable {
  constructor(el, domainName, dragType, dragData, dragPreviewEl) {
    this.el = el;
    this.domainName = domainName;
    this.dragType = dragType;
    this.dragData = dragData;
    this.dragPreviewEl = dragPreviewEl;

    this.startListening = this.startListening.bind(this);
    this.stopListening = this.stopListening.bind(this);
    this.handleGestureStart = this.handleGestureStart.bind(this);
    this.handleGestureMove = this.handleGestureMove.bind(this);
    this.queueRedraw = this.queueRedraw.bind(this);
    this.onAnimFrame = this.onAnimFrame.bind(this);
    this.handleGestureEnd = this.handleGestureEnd.bind(this);
    this.handleContextMenu = this.handleContextMenu.bind(this);
  }

  // PUBLIC
  startListening() {
    // Taken from:
    // https://developers.google.com/web/fundamentals/design-and-ux/input/touch/

    // Add Touch Listener
    this.el.addEventListener('touchstart', this.handleGestureStart, true);
    this.el.addEventListener('touchmove', this.handleGestureMove, true);
    this.el.addEventListener('touchend', this.handleGestureEnd, true);
    this.el.addEventListener('touchcancel', this.handleGestureEnd, true);

    // Add Mouse Listener
    this.el.addEventListener('mousedown', this.handleGestureStart, true);

    // Add context menu listener
    this.el.addEventListener('contextmenu', this.handleContextMenu, true);

    // Disable touch actions except pinch-zoom
    this.el.style.setProperty('touch-action', 'pinch-zoom');

    Draggable.applyInitialStyles(this.dragPreviewEl);
    this.dpeDimensions = Draggable.calculateDpeDimensions(this.dragPreviewEl);
  }

  // PUBLIC
  stopListening() {
    // Taken from:
    // https://developers.google.com/web/fundamentals/design-and-ux/input/touch/

    // Remove Touch Listener
    this.el.removeEventListener('touchstart', this.handleGestureStart, true);
    this.el.removeEventListener('touchmove', this.handleGestureMove, true);
    this.el.removeEventListener('touchend', this.handleGestureEnd, true);
    this.el.removeEventListener('touchcancel', this.handleGestureEnd, true);

    // Remove Mouse Listener
    this.el.removeEventListener('mousedown', this.handleGestureStart, true);

    // Remove context menu listener
    this.el.removeEventListener('contextmenu', this.handleContextMenu, true);
  }

  handleGestureStart(evt) {
    // Taken from:
    // https://developers.google.com/web/fundamentals/design-and-ux/input/touch/

    evt.preventDefault();

    if (evt.touches && evt.touches.length > 1) {
      return;
    }

    // Add the move and end listeners
    // Add Mouse Listeners
    document.addEventListener('mousemove', this.handleGestureMove, true);
    document.addEventListener('mouseup', this.handleGestureEnd, true);

    dispatch(startDrag(this.domainName, this.dragType, this.dragData));
    Draggable.toggleGrabbingStyle(this.dragPreviewEl, true);
    HitAreaManager.addTransparentDivs();

    const lastPointerPos = Draggable.getGesturePointFromEvent(evt);
    this.queueRedraw(lastPointerPos);
    pointerPosStore.updateLastPointerPos(this.domainName, lastPointerPos);
  }

  handleGestureMove(evt) {
    // Taken from:
    // https://developers.google.com/web/fundamentals/design-and-ux/input/touch/

    evt.preventDefault();

    const state = getState();
    if (!isDragging(state, this.domainName)) {
      return;
    }

    const lastPointerPos = Draggable.getGesturePointFromEvent(evt);
    this.queueRedraw(lastPointerPos);
    pointerPosStore.updateLastPointerPos(this.domainName, lastPointerPos);
  }

  queueRedraw(lastPointerPos) {
    this.lastPos = lastPointerPos;
    if (this.rafPending) {
      return;
    }
    this.rafPending = true;
    window.requestAnimationFrame(this.onAnimFrame);
  }

  onAnimFrame() {
    if (!this.rafPending) {
      return;
    }

    this.dragPreviewEl.style.setProperty('top', `${this.lastPos.y - (this.dpeDimensions.h / 2)}px`);
    this.dragPreviewEl.style.setProperty('left', `${this.lastPos.x - (this.dpeDimensions.w / 2)}px`);
    this.dragPreviewEl.style.setProperty('visibility', 'visible');

    this.rafPending = false;
  }

  handleGestureEnd(evt) {
    // Taken from:
    // https://developers.google.com/web/fundamentals/design-and-ux/input/touch/

    evt.preventDefault();

    if (evt.touches && evt.touches.length > 0) {
      return;
    }

    dispatch(stopDrag(this.domainName));

    this.rafPending = false;

    // Remove Mouse Listeners
    document.removeEventListener('mousemove', this.handleGestureMove, true);
    document.removeEventListener('mouseup', this.handleGestureEnd, true);

    this.dragPreviewEl.style.setProperty('visibility', 'hidden');
    Draggable.toggleGrabbingStyle(this.dragPreviewEl, false);
    HitAreaManager.removeTransparentDivs();
  }

  handleContextMenu(evt) {
    // Disable context menu on this element so that we get better down/up events
    evt.preventDefault();
  }

  static getGesturePointFromEvent(evt) {
    // Taken from:
    // https://developers.google.com/web/fundamentals/design-and-ux/input/touch/

    const point = {};

    if (evt.targetTouches) {
      // Prefer Touch Events
      point.x = evt.targetTouches[0].clientX;
      point.y = evt.targetTouches[0].clientY;
    } else {
      // Mouse event
      point.x = evt.clientX;
      point.y = evt.clientY;
    }

    return point;
  }

  static applyInitialStyles(dragPreviewEl) {
    dragPreviewEl.style.setProperty('position', 'fixed');
    dragPreviewEl.style.setProperty('visibility', 'hidden');
    dragPreviewEl.style.setProperty('z-index', '5000'); // Same as in react-beautiful-dnd
  }

  static toggleGrabbingStyle(dragPreviewEl, on) {
    dragPreviewEl.style.setProperty('cursor', on ? 'grabbing' : 'auto');
  }

  static calculateDpeDimensions(dragPreviewEl) {
    return {
      w: dragPreviewEl.offsetWidth,
      h: dragPreviewEl.offsetHeight,
    };
  }
}

export default Draggable;
